import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../components/AuthHero';
import { applicationApiService } from '../services/applicationApi';
import FlowGuard from '../../components/FlowGuard';

const ApplicationOTPContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email || '';
    const applicationId = location.state?.applicationId || '';
    const firstName = location.state?.firstName || '';

    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [resendTimer, setResendTimer] = useState(60);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Focus first input on mount
        inputRefs.current[0]?.focus();

        // Start countdown timer
        const interval = setInterval(() => {
            setResendTimer(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const handleOtpChange = (index: number, value: string) => {
        // Only allow numbers
        if (value && !/^\d$/.test(value)) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        if (!/^\d{6}$/.test(pastedData)) {
            return;
        }

        const newOtp = pastedData.split('');
        setOtp(newOtp);
        inputRefs.current[5]?.focus();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const otpString = otp.join('');
        if (otpString.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        if (!applicationId) {
            setError('Invalid application. Please submit your application again.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Verify OTP using API service
            await applicationApiService.verifyOTP(applicationId, otpString);

            // Navigate to success page with flow state
            navigate('/auth/application-success', { state: { fromJobApplication: true } });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendTimer > 0) {
            return;
        }

        if (!applicationId) {
            setError('Invalid application. Please submit your application again.');
            return;
        }

        try {
            // Resend OTP using API service
            await applicationApiService.resendOTP(applicationId);

            setResendTimer(60);
            setError('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Form */}
            <div className="w-full lg:flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            onClick={() => navigate('/')}
                            style={{
                                width: '120px',
                                height: '55px',
                                objectFit: 'contain',
                                cursor: 'pointer'
                            }}
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '32px',
                            color: '#015FA6',
                            marginBottom: '8px'
                        }}
                    >
                        Verify your email
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'SF Pro, Manrope',
                            fontSize: '14px',
                            color: '#015FA6',
                            marginBottom: '32px'
                        }}
                    >
                        We've sent a 6-digit code to{' '}
                        <strong style={{ fontWeight: 600 }}>{email}</strong>
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* OTP Input */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#015FA6',
                                    display: 'block',
                                    marginBottom: '8px'
                                }}
                            >
                                Enter verification code
                            </label>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(6, 1fr)',
                                    gap: '12px'
                                }}
                                onPaste={handlePaste}
                            >
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        style={{
                                            width: '100%',
                                            height: '60px',
                                            fontFamily: 'Manrope',
                                            fontSize: '28px',
                                            fontWeight: 600,
                                            textAlign: 'center',
                                            color: '#015FA6',
                                            background: '#F7FAFC',
                                            border: error ? '2px solid #EF4444' : '2px solid #CBD5E0',
                                            borderRadius: '12px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s ease',
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#015FA6';
                                            e.target.select();
                                        }}
                                        onBlur={(e) => {
                                            if (!error) {
                                                e.target.style.borderColor = '#CBD5E0';
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                            {error && (
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#EF4444',
                                        marginTop: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path
                                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                            stroke="currentColor"
                                            strokeWidth={1.5}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    {error}
                                </p>
                            )}
                        </div>

                        {/* Resend Code */}
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: '24px'
                            }}
                        >
                            <p
                                style={{
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    color: '#015FA6',
                                }}
                            >
                                Didn't receive the code?{' '}
                                {resendTimer > 0 ? (
                                    <span style={{ fontWeight: 500 }}>
                                        Resend in {resendTimer}s
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#015FA6',
                                            fontWeight: 600,
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            fontFamily: 'Manrope',
                                            fontSize: '14px',
                                            padding: 0
                                        }}
                                    >
                                        Resend code
                                    </button>
                                )}
                            </p>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading || otp.join('').length !== 6}
                            style={{
                                width: '100%',
                                padding: '14px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#F7FAFC',
                                background: isLoading || otp.join('').length !== 6 ? '#9CA3AF' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading || otp.join('').length !== 6 ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease',
                                marginBottom: '16px',
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading && otp.join('').length === 6) {
                                    e.currentTarget.style.background = '#014880';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading && otp.join('').length === 6) {
                                    e.currentTarget.style.background = '#015FA6';
                                }
                            }}
                        >
                            {isLoading ? 'Verifying...' : 'Verify'}
                        </button>

                        {/* Back Button */}
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#015FA6',
                                background: '#FFFFFF',
                                border: '1px solid #CBD5E0',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                        >
                            Back
                        </button>
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="hidden lg:flex lg:flex-1" style={{ background: '#015FA6' }}>
                <AuthHero />
            </div>
        </div>
    );
};

/**
 * ApplicationOTP Page
 * Protected by FlowGuard - only accessible after application submission
 * Direct URL access redirects to /auth/apply
 * Route: /auth/application-otp
 */
const ApplicationOTP: React.FC = () => {
    return (
        <FlowGuard
            requiredStateKey="applicationId"
            fallbackPath="/auth/apply"
        >
            <ApplicationOTPContent />
        </FlowGuard>
    );
};

export default ApplicationOTP;
